import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Database, ArrowRight, AlertTriangle } from 'lucide-react'
import cd2bPropuestaService from '@/services/cd2bPropuestaService'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { ESTADO_PROYECTO, resumenMigracion } from '@/pages/ConsultorMigracion/estados'

/**
 * La migración, en la ficha de la cotización, con ESTADO.
 *
 * Antes era un botón `outline` entre otros tres iguales, y los cuatro no son del mismo
 * rango: uno es de ciclo de vida (crear la demo), otro una consulta (ver acceso), otro un
 * trámite ajeno (pedir un plugin) y este un PROCESO CON ESTADO PROPIO que dura días. Verlo
 * como un botón más obligaba a entrar para saber si iba por la mitad o ni había empezado.
 *
 * Cuando no hay nada, no ocupa: una línea con la invitación a empezar. Cuando hay algo,
 * enseña por dónde va sin entrar — y si hay errores, lo dice en rojo, que es la única razón
 * por la que hay que volver.
 */
export default function TarjetaMigracion({ propuestaId, deshabilitado = false, enProduccion = false }) {
  const navigate = useNavigate()
  const [proyecto, setProyecto] = useState(null)
  const [cargado, setCargado] = useState(false)

  const cargar = useCallback(async () => {
    try {
      const r = await cd2bPropuestaService.listarProyectosMigracion(propuestaId)
      const lista = r?.data || []
      // El más reciente: es el que el asesor tiene entre manos.
      setProyecto(lista[0] || null)
    } catch {
      // Sin proyecto no hay tarjeta con estado, pero la entrada tiene que seguir estando:
      // que el listado falle no puede dejar al asesor sin puerta a la migración.
      setProyecto(null)
    } finally {
      setCargado(true)
    }
  }, [propuestaId])

  useEffect(() => {
    cargar()
  }, [cargar])

  const ir = () => navigate(`/consultor/cotizaciones/${propuestaId}/migracion`)
  if (!cargado) return null

  const estado = proyecto ? ESTADO_PROYECTO[proyecto.estado] : null
  const resumen = resumenMigracion(proyecto)
  const cargando = proyecto?.estado === 'cargando'
  const pct =
    cargando && proyecto?.totales?.hojas
      ? Math.round(((proyecto.totales.hojasCargadas || 0) / proyecto.totales.hojas) * 100)
      : null
  const conErrores = (proyecto?.hojas || []).some((h) => h.carga?.errores > 0 || h.ensayo?.errores > 0)

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-medium text-neutral-900 dark:text-neutral-50">
            <Database size={16} className="shrink-0 text-neutral-400 dark:text-neutral-500" />
            Datos del sistema anterior
            {estado && <Badge variant={estado.variante}>{estado.etiqueta}</Badge>}
          </p>
          {resumen ? (
            <p className="mt-0.5 flex items-center gap-1.5 text-caption text-neutral-500 dark:text-neutral-400">
              {conErrores && <AlertTriangle size={13} className="shrink-0 text-danger" />}
              {resumen}
            </p>
          ) : (
            <p className="mt-0.5 text-caption text-neutral-500 dark:text-neutral-400">
              Trae su catálogo, su cartera y sus saldos antes de entregar la empresa.
            </p>
          )}
          {pct != null && <Progress value={pct} className="mt-2 max-w-xs" />}
          {enProduccion && (
            <p className="mt-0.5 text-caption text-neutral-500 dark:text-neutral-400">
              La empresa ya está en producción: lo importado queda como está y ya no se carga desde aquí.
            </p>
          )}
        </div>

        <Button
          variant={proyecto && !enProduccion ? 'default' : 'outline'}
          onClick={ir}
          disabled={deshabilitado}
          className="shrink-0"
        >
          {enProduccion ? 'Ver' : proyecto ? 'Continuar' : 'Empezar'}
          <ArrowRight size={16} className="ml-1.5" />
        </Button>
      </CardContent>
    </Card>
  )
}
